import React, { useEffect, useMemo, useRef, useState } from "react";
import { MoreOutlined } from "@ant-design/icons";
import { Dropdown, Modal, Select } from "antd";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { useDispatch, useSelector } from "react-redux";
import { getAllLeavesAPI } from "../../../Redux/Apis/leaveApi";
import { getEmployeesAPI } from "../../../Redux/Apis/employeeApi";
import { checkPermission, hasAdminAccess, permissionType } from "../../../app/utils/common/Functions";
import { sendInvoiceEmail } from "../../../app/utils/common/invoiceEmail";
import {
  buildInvoiceMailPlainText,
  buildInvoiceMailSubject,
  getInvoiceMailAttachmentNames,
} from "../../../app/utils/common/invoiceMailto";
import { fetchEmployeeTimesheetPdf } from "../../../app/utils/common/invoiceTimesheet";
import { protectedApi } from "../../../app/utils/common/protectedApi";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import { APIS } from "../../../app/utils/constants/apiConstant";
import ConfirmModal from "../../atoms/ConfirmModal/ConfirmModal";
import Loader from "../../atoms/Loader/Loader";
import { COMPANY_PROFILES_UPDATED_EVENT } from "../../../app/utils/company/companyProfilesStorage";
import { fetchCompanyProfiles } from "../../../app/utils/billing/companyDetailsApi";
import { fetchClientProfiles } from "../../../app/utils/billing/clientManagementApi";
import {
  createInvoiceApi,
  deleteInvoiceApi,
  fetchInvoices,
  updateInvoiceApi,
} from "../../../app/utils/billing/invoiceManagementApi";
import { amountToIndianWords } from "../../../app/utils/finance/salarySlipPdf";

const initialForm = {
  invoiceNumber: "",
  invoiceDate: "",
  invoiceMonth: "",
  clientId: "",
  companyId: "",
  gstNumber: "",
  hsnCode: "",
  companyAddress: "",
  clientDetails: "",
  companyDetails: "",
  bankDetails: "",
  templateDetails: "Standard invoice template",
  gstPercentage: 18,
  resources: [],
  attachedTimesheets: [],
};

const initialResource = {
  employeeId: "",
  employeeName: "",
  price: "",
  quantity: 1,
  note: "",
};

const getTodayInputValue = () => {
  const today = new Date();
  return today.toISOString().slice(0, 10);
};

const getCurrentMonthInputValue = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
};

const formatMonth = (value) => {
  if (!/^\d{4}-\d{2}$/.test(String(value || ""))) return "";
  const [year, month] = value.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
  });
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-IN");
};

const formatDateNumber = (date) =>
  `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;

const formatMonthDateRange = (value) => {
  if (!/^\d{4}-\d{2}$/.test(String(value || ""))) return formatMonth(value);
  const [year, month] = value.split("-").map(Number);
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0);
  return `${formatDateNumber(start)} - ${formatDateNumber(end)}`;
};

const parseDateOnly = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  const datePart = raw.includes("T") ? raw.split("T")[0] : raw.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    const [year, month, day] = datePart.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const getMonthBounds = (invoiceMonth) => {
  if (!/^\d{4}-\d{2}$/.test(String(invoiceMonth || ""))) return null;
  const [year, month] = invoiceMonth.split("-").map(Number);
  return {
    start: new Date(year, month - 1, 1),
    end: new Date(year, month, 0),
  };
};

const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

const countWorkingDaysBetween = (start, end) => {
  let count = 0;
  const current = new Date(start);
  while (current <= end) {
    if (!isWeekend(current)) count += 1;
    current.setDate(current.getDate() + 1);
  }
  return count;
};

const getWorkingDaysInMonth = (invoiceMonth) => {
  const bounds = getMonthBounds(invoiceMonth);
  if (!bounds) return 1;
  return countWorkingDaysBetween(bounds.start, bounds.end);
};

const normalizeName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const getLeaveEmployeeId = (leave) =>
  String(leave?.employeeId ?? leave?.userId ?? leave?.user_id ?? leave?.user?.id ?? "");

const getLeaveEmployeeName = (leave) =>
  normalizeName(
    leave?.employeeName ||
    leave?.userName ||
    `${leave?.userFirstname || ""} ${leave?.userLastname || ""}`
  );

const resourceMatchesLeave = (resource, leave) => {
  const resourceEmployeeId = String(resource?.employeeId || "");
  const leaveEmployeeId = getLeaveEmployeeId(leave);
  if (resourceEmployeeId && leaveEmployeeId && resourceEmployeeId === leaveEmployeeId) return true;
  return normalizeName(resource?.employeeName) === getLeaveEmployeeName(leave);
};

const getLeaveDayMultiplier = (leave) => {
  const type = String(leave?.leaveType || "").toLowerCase();
  if (type.includes("half") || type.includes("early")) return 0.5;
  return 1;
};

const calculateApprovedLeaveDays = (resource, invoiceMonth, leaves) => {
  const bounds = getMonthBounds(invoiceMonth);
  if (!bounds) return 0;

  return (Array.isArray(leaves) ? leaves : []).reduce((total, leave) => {
    if (String(leave?.status || "").toLowerCase() !== "approved") return total;
    if (!resourceMatchesLeave(resource, leave)) return total;

    const leaveStart = parseDateOnly(leave.startDate);
    const leaveEnd = parseDateOnly(leave.endDate) || leaveStart;
    if (!leaveStart || !leaveEnd) return total;

    const overlapStart = leaveStart > bounds.start ? leaveStart : bounds.start;
    const overlapEnd = leaveEnd < bounds.end ? leaveEnd : bounds.end;
    if (overlapStart > overlapEnd) return total;

    return total + countWorkingDaysBetween(overlapStart, overlapEnd) * getLeaveDayMultiplier(leave);
  }, 0);
};

const roundCurrency = (value) => Math.round((Number(value) || 0) * 100) / 100;

const calculateAmountForDays = (monthlyPrice, payableDays, workingDays) => {
  const price = Number(monthlyPrice) || 0;
  const days = Number(payableDays) || 0;
  const divisor = Number(workingDays) || 0;
  if (divisor <= 0) return price;
  return roundCurrency((price / divisor) * days);
};

const applyCalculatedResourceDays = (resources, invoiceMonth, leaves) => {
  const workingDays = getWorkingDaysInMonth(invoiceMonth);
  return (Array.isArray(resources) ? resources : []).map((resource) => {
    const leaveDays = calculateApprovedLeaveDays(resource, invoiceMonth, leaves);
    const calculatedDays = Math.max(0, workingDays - leaveDays);
    const price = Number(resource.price) || 0;
    return {
      ...resource,
      quantity: calculatedDays,
      totalAmount: calculateAmountForDays(price, calculatedDays, workingDays),
      workingDays,
      leaveDays,
      calculatedDays,
    };
  });
};

const formatMoney = (value) => {
  const number = Number(value) || 0;
  return `₹${number.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const getDetailRows = (value) => {
  const lines = String(value || "").split("\n");
  const wrappedRows = lines.reduce(
    (count, line) => count + Math.max(1, Math.ceil(line.length / 70)),
    0
  );
  return Math.max(3, wrappedRows);
};

const getCompanyLabel = (company) =>
  company?.companyName || company?.name || company?.emailId || "Company";

const getClientLabel = (client) =>
  client?.clientName || client?.companyName || client?.emailId || "Client";

const getRecordId = (record, index, fallbackPrefix) =>
  String(record?.id || record?.gstNumber || record?.emailId || `${fallbackPrefix}-${index}`);

const buildBankDetails = (company) => {
  if (!company) return "";
  return [
    company.bankName ? `Bank: ${company.bankName}` : "",
    company.accountHolderName ? `Account Holder: ${company.accountHolderName}` : "",
    company.accountNumber ? `Account No: ${company.accountNumber}` : "",
    company.ifscCode ? `IFSC: ${company.ifscCode}` : "",
    company.branchName ? `Branch: ${company.branchName}` : "",
  ]
    .filter(Boolean)
    .join("\n");
};

const buildCompanyDetails = (company) => {
  if (!company) return "";
  return [
    company.ownerName ? `${company.ownerName} - ${getCompanyLabel(company)}` : getCompanyLabel(company),
    company.companyAddress || "",
    company.contactNumber || "",
    company.emailId || "",
  ]
    .filter(Boolean)
    .join("\n");
};

const buildClientDetails = (client) => {
  if (!client) return "";
  return [
    getClientLabel(client),
    client.emailId ? `Email: ${client.emailId}` : "",
    client.contactNumber ? `Phone: ${client.contactNumber}` : "",
    client.companyAddress ? `Address: ${client.companyAddress}` : "",
  ]
    .filter(Boolean)
    .join("\n");
};

const normalizeResources = (resources) =>
  (Array.isArray(resources) ? resources : []).map((resource, index) => {
    const price = Number(resource.price ?? resource.resourcePrice ?? resource.amount ?? 0);
    const totalAmount = Number(resource.totalAmount ?? resource.amount ?? price);
    const quantity = Number(resource.quantity ?? resource.duration ?? 1);
    return {
      id: String(resource.id || resource.employeeId || `resource-${Date.now()}-${index}`),
      employeeId: resource.employeeId ? String(resource.employeeId) : "",
      employeeName: resource.employeeName || resource.name || `Resource ${index + 1}`,
      price: Number.isNaN(price) ? 0 : price,
      totalAmount: Number.isNaN(totalAmount) ? (Number.isNaN(price) ? 0 : price) : totalAmount,
      quantity: Number.isNaN(quantity) || quantity < 0 ? 1 : quantity,
      note: String(resource.note || "").trim(),
    };
  });

const getResourceTotalAmount = (resource) => {
  const totalAmount = Number(resource?.totalAmount);
  if (!Number.isNaN(totalAmount)) return totalAmount;
  return Number(resource?.price) || 0;
};

const calculateTotals = (resources, gstPercentage) => {
  const subtotal = (Array.isArray(resources) ? resources : []).reduce((sum, resource) => {
    return sum + getResourceTotalAmount(resource);
  }, 0);
  const gst = subtotal * ((Number(gstPercentage) || 0) / 100);
  return {
    subtotal,
    gst,
    totalAmount: subtotal,
    grandTotal: subtotal + gst,
  };
};

/** Per-company sequence: 01, 02, 03, … (no year or company prefix in the number). */
const parseInvoiceSequence = (invoiceNumber) => {
  const value = String(invoiceNumber || "").trim();
  if (!value) return 0;

  const simple = value.match(/^(\d+)$/);
  if (simple) return Number(simple[1]) || 0;

  const companyYear = value.match(/^(\d{2})-(\d{4})-(\d+)$/);
  if (companyYear) return Number(companyYear[3]) || 0;

  const yearOnly = value.match(/^(?:INV-)?(\d{4})-(\d+)$/);
  if (yearOnly) return Number(yearOnly[2]) || 0;

  return 0;
};

const generateInvoiceNumber = (invoices, companyId) => {
  const companyIdStr = String(companyId || "").trim();
  if (!companyIdStr) return "";

  const maxSequence = (Array.isArray(invoices) ? invoices : []).reduce((max, invoice) => {
    if (String(invoice.companyId || "") !== companyIdStr) return max;
    return Math.max(max, parseInvoiceSequence(invoice.invoiceNumber));
  }, 0);

  return String(maxSequence + 1).padStart(2, "0");
};

const resolveAutoInvoiceNumber = ({
  editingInvoiceId,
  isInvoiceNumberEdited,
  invoices,
  companyId,
  currentNumber,
}) => {
  if (editingInvoiceId || isInvoiceNumberEdited) return currentNumber;
  return generateInvoiceNumber(invoices, companyId);
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const isValidEmailList = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return true;
  return raw
    .split(",")
    .map((email) => email.trim())
    .filter(Boolean)
    .every((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
};

const GUJARAT_GST_STATE_CODE = "24";

const isClientInGujarat = (invoice = {}) => {
  const clientGstNumber = String(invoice.clientGstNumber || "").trim().toUpperCase();
  if (/^\d{2}/.test(clientGstNumber)) {
    return clientGstNumber.slice(0, 2) === GUJARAT_GST_STATE_CODE;
  }

  const address = String(invoice.clientAddress || "").toLowerCase();
  return /\bgujarat\b|\bgujrat\b/.test(address);
};

const getInvoiceGstBreakdown = (invoice = {}) => {
  const gstPercentage = Number(invoice.gstPercentage) || 0;
  const subtotal = Number(invoice.subtotal) || 0;
  const storedGst = Number(invoice.gst);
  const totalTax = Number.isFinite(storedGst) && storedGst >= 0
    ? storedGst
    : subtotal * (gstPercentage / 100);
  const intraState = isClientInGujarat(invoice);

  if (intraState) {
    const halfRate = gstPercentage / 2;
    const halfAmount = totalTax / 2;
    return {
      intraState: true,
      subtotal,
      igstRate: 0,
      igstAmount: 0,
      cgstRate: halfRate,
      cgstAmount: halfAmount,
      sgstRate: halfRate,
      sgstAmount: halfAmount,
      totalTax,
    };
  }

  const halfRate = gstPercentage / 2;
  return {
    intraState: false,
    subtotal,
    igstRate: gstPercentage,
    igstAmount: totalTax,
    cgstRate: halfRate,
    cgstAmount: 0,
    sgstRate: halfRate,
    sgstAmount: 0,
    totalTax,
  };
};

const formatGstRate = (rate) => (Number(rate) > 0 ? `${rate}%` : "0%");

const formatAmountInWords = (amount) =>
  amountToIndianWords(amount)
    .replace(/\b\w/g, (character) => character.toUpperCase());

const INVOICE_TAX_COMPONENTS = [
  { key: "igst", label: "IGST" },
  { key: "cgst", label: "CGST" },
  { key: "sgst", label: "SGST" },
];

/** Inter-state: IGST + CGST + SGST (CGST/SGST at half rate, zero amount). Intra-state: CGST + SGST only. */
const getInvoiceTaxComponents = (gstBreakdown = {}) =>
  gstBreakdown.intraState
    ? INVOICE_TAX_COMPONENTS.filter(({ key }) => key === "cgst" || key === "sgst")
    : INVOICE_TAX_COMPONENTS;

const buildTaxTableMarkup = (gstBreakdown, hsnCode) => {
  const taxComponents = getInvoiceTaxComponents(gstBreakdown);

  const headerGroups = taxComponents
    .map(({ label }) => `<th colspan="2">${label}</th>`)
    .join("");
  const subHeaders = taxComponents.map(() => `<th>Rate</th><th>Amount</th>`).join("");
  const valueCells = taxComponents
    .map(({ key }) => {
      const rate = gstBreakdown[`${key}Rate`];
      const amount = gstBreakdown[`${key}Amount`];
      return `<td>${escapeHtml(formatGstRate(rate))}</td><td>${escapeHtml(formatMoney(amount))}</td>`;
    })
    .join("");

  return `
      <table class="tax-table">
        <thead>
          <tr>
            <th rowspan="2">HSN</th>
            <th rowspan="2">Taxable Value</th>
            ${headerGroups}
          </tr>
          <tr>
            ${subHeaders}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>${escapeHtml(hsnCode)}</td>
            <td>${escapeHtml(formatMoney(gstBreakdown.subtotal))}</td>
            ${valueCells}
          </tr>
        </tbody>
      </table>`;
};

const buildInvoiceHtml = (invoice, options = {}) => {
  const { autoPrint = false, hideActions = false } = options;
  const gstBreakdown = getInvoiceGstBreakdown(invoice);
  const totalInWords = formatAmountInWords(invoice.grandTotal);
  const resourceRows = invoice.resources
    .map(
      (resource, index) => `
        <tr>
          <td class="description">Payment for (${escapeHtml(formatMonthDateRange(invoice.invoiceMonth))}) - ${escapeHtml(resource.employeeName)} - Technical Services</td>
          <td>${escapeHtml(resource.quantity)}</td>
          <td>${escapeHtml(invoice.hsnCode)}</td>
          <td>${escapeHtml(formatMonth(invoice.invoiceMonth))}</td>
          <td class="amount">${escapeHtml(formatMoney(getResourceTotalAmount(resource)))}</td>
        </tr>`
    )
    .join("");
  const fillerRows = Array.from({ length: Math.max(8 - invoice.resources.length, 2) })
    .map(
      () => `
        <tr class="blank-row">
          <td></td><td></td><td></td><td></td><td></td>
        </tr>`
    )
    .join("");
  const billToDetails = [
    invoice.clientCompanyName || invoice.clientName,
    invoice.clientGstNumber ? `GST - ${invoice.clientGstNumber}` : "",
    invoice.clientAddress || "",
  ]
    .filter(Boolean)
    .join("\n");
  const shipToDetails = billToDetails;
  const companyTaxDetails = [
    invoice.companyGstNumber ? `GST No - ${invoice.companyGstNumber}` : "",
    invoice.companyPanNumber ? `PAN No - ${invoice.companyPanNumber}` : "",
  ]
    .filter(Boolean)
    .join("\n");
  const companyHeaderDetails = [
    invoice.companyOwnerName ? `${invoice.companyOwnerName} - ${invoice.companyName}` : invoice.companyName,
    invoice.companyAddress,
    invoice.companyPhone,
    invoice.companyEmail,
  ]
    .filter(Boolean)
    .join("\n") || String(invoice.companyDetails || "").replace(/Phone:\s*/g, "").replace(/Email:\s*/g, "");

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(invoice.invoiceNumber)}</title>
  <style>
    @page { size: A4 landscape; margin: 8mm; }
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { font-family: Arial, sans-serif; color: #111; margin: 0; background: #fff; font-size: 12px; }
    .invoice-sheet { width: 1120px; max-width: 100%; margin: 0 auto; padding: 16px 24px 24px; overflow: hidden; }
    .print-actions { margin: 0 0 16px; }
    .print-actions button { background: #c40000; border: 0; color: #fff; padding: 8px 14px; border-radius: 4px; cursor: pointer; }
    .red-bar { height: 16px; background: #d50000; box-shadow: inset 0 0 0 999px #d50000; margin-bottom: 70px; }
    .title-row { display: grid; grid-template-columns: 1fr 260px; align-items: start; gap: 32px; margin-bottom: 48px; }
    h1 { margin: 0 0 55px; font-size: 28px; font-weight: 500; color: #6a6a6a; letter-spacing: .3px; }
    .company-block, .address-block { white-space: pre-line; line-height: 1.45; }
    .invoice-meta { text-align: center; margin-top: 98px; font-size: 11px; }
    .invoice-meta .date { border-bottom: 1px solid #999; padding: 0 0 10px; margin-bottom: 8px; font-weight: 700; }
    .invoice-meta .number { border-bottom: 1px solid #999; padding-bottom: 10px; font-weight: 700; }
    .party-row { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; margin-bottom: 28px; }
    .section-label { border-bottom: 1px solid #d7d7d7; padding-bottom: 8px; margin-bottom: 8px; font-weight: 700; color: #4d4d4d; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #d50000; box-shadow: inset 0 0 0 999px #d50000; color: #fff; font-weight: 700; text-align: center; padding: 5px 8px; border: 1px solid #8f8f8f; font-size: 11px; vertical-align: middle; line-height: 1.2; }
    td { border: 1px solid #a9a9a9; padding: 7px 8px; vertical-align: middle; line-height: 1.25; }
    .line-items .description { width: 52%; }
    .line-items td:not(.description), .tax-table td { text-align: center; }
    .amount { text-align: right !important; white-space: nowrap; }
    .blank-row td { height: 28px; background: #f4f4f4; }
    .subtotal-row { display: grid; grid-template-columns: minmax(0, 1fr) 210px; margin: 18px 0 24px; align-items: center; }
    .subtotal-box { display: grid; grid-template-columns: 95px 115px; border: 1px solid #000; border-bottom: 0; width: 210px; }
    .subtotal-box div { padding: 8px 6px; border-bottom: 1px solid #000; font-weight: 700; overflow: hidden; display: flex; align-items: center; justify-content: center; }
    .subtotal-box div:nth-child(odd) { border-right: 1px solid #000; text-align: center; }
    .subtotal-box div:nth-child(even), .tax-total .value, .payment-section .total-footer-row div:last-child { font-size: 11px; line-height: 1.2; white-space: nowrap; text-align: center; }
    .tax-area { display: grid; grid-template-columns: minmax(0, 1fr) 210px; align-items: stretch; margin-bottom: 0; }
    .tax-table th { background: #fff; box-shadow: none; color: #111; border: 1px solid #000; vertical-align: middle !important; text-align: center; padding: 0 8px; }
    .tax-table thead tr:first-child th { height: 30px; line-height: 40px; }
    .tax-table thead tr:first-child th[rowspan] { height: 54px; line-height: 54px; }
    .tax-table thead tr:nth-child(2) th { height: 24px; line-height: 24px; }
    .tax-table td { border: 1px solid #000; padding: 12px 8px; vertical-align: middle !important; text-align: center; }
    .tax-total { border: 1px solid #000; border-left: 0; display: flex; flex-direction: column; }
    .tax-total .label { padding: 22px 8px; text-align: center; border-bottom: 1px solid #000; display: flex; align-items: center; justify-content: center; }
    .tax-total .value { padding: 18px 8px; text-align: center; font-weight: 700; display: flex; align-items: center; justify-content: center; }
    .payment-section { display: grid; grid-template-columns: minmax(0, 1fr) 210px; grid-template-rows: minmax(250px, 1fr) auto; min-height: 250px; }
    .payment-left { grid-column: 1; grid-row: 1; border-left: 1px solid #000; padding: 16px 0 0; }
    .remarks { margin-bottom: 34px; }
    .company-tax { text-align: center; font-weight: 700; white-space: pre-line; margin-bottom: 60px; }
    .bank { text-align: center; white-space: pre-line; margin-bottom: 58px; }
    .bank-label { font-size: 11px; margin-left: 0; }
    .grand-total { grid-column: 2; grid-row: 1; border-left: 1px solid #000; border-right: 1px solid #000; }
    .payment-section .total-footer-row { grid-column: 1 / -1; grid-row: 2; display: grid; grid-template-columns: minmax(0, 1fr) 120px minmax(0, 1fr) 208px; width: 100%; border: 1px solid #000; border-top: 1px solid #000; font-weight: 700; min-height: 42px; }
    .payment-section .total-footer-row div { padding: 8px 6px; display: flex; align-items: center; justify-content: center; text-align: center; border-right: 1px solid #000; }
    .payment-section .total-footer-row div:last-child { border-right: 0; }
    .payment-section .total-footer-row .words-value { justify-content: flex-start; text-align: left; font-size: 12px; line-height: 1.3; font-weight: 600; text-transform: capitalize; padding-left: 8px; padding-right: 8px; }
    .signature { margin-top: 26px; font-size: 11px; }
    .signature img { display: block; max-width: 170px; max-height: 70px; margin: 8px 0; }
    .bottom-bar { height: 18px; background: #d50000; box-shadow: inset 0 0 0 999px #d50000; margin-top: 18px; }
    @media print {
      body { margin: 0; }
      body { font-size: 9.5px; }
      .invoice-sheet { width: 100%; max-width: none; padding: 0; overflow: visible; }
      .print-actions { display: none; }
      .red-bar { height: 10px; margin-bottom: 22px; }
      .title-row { grid-template-columns: minmax(0, 1fr) 220px; }
      .title-row { margin-bottom: 18px; gap: 24px; }
      h1 { font-size: 20px; margin-bottom: 22px; }
      .invoice-meta { margin-top: 46px; font-size: 9px; }
      .invoice-meta .date { padding-bottom: 6px; margin-bottom: 5px; }
      .invoice-meta .number { padding-bottom: 6px; }
      .party-row { gap: 36px; margin-bottom: 16px; }
      .section-label { padding-bottom: 5px; margin-bottom: 5px; }
      th { padding: 3px 5px; font-size: 9px; }
      td { padding: 4px 5px; }
      .tax-table th { padding: 0 5px; }
      .tax-table thead tr:first-child th { height: 24px; line-height: 24px; }
      .tax-table thead tr:first-child th[rowspan] { height: 44px; line-height: 44px; }
      .tax-table thead tr:nth-child(2) th { height: 20px; line-height: 20px; }
      .blank-row td { height: 18px; }
      .subtotal-row { margin: 10px 0 12px; }
      .tax-table td { padding: 7px 5px; }
      .tax-total .label { padding: 13px 6px; }
      .tax-total .value { padding: 11px 6px; }
      .payment-section { min-height: 150px; }
      .payment-left { padding-top: 10px; }
      .company-tax { margin-bottom: 30px; }
      .bank { margin-bottom: 24px; }
      .payment-section .total-footer-row { min-height: 30px; }
      .payment-section .total-footer-row div { padding: 7px 5px; }
      .payment-section .total-footer-row .words-value { font-size: 8px; }
      .signature { margin-top: 12px; font-size: 9px; }
      .signature img { max-height: 42px; margin: 5px 0; }
      .bottom-bar { height: 10px; margin-top: 8px; }
    }
  </style>
</head>
<body>
  <div class="invoice-sheet">
    ${hideActions
      ? ""
      : `<div class="print-actions"><button onclick="window.opener && window.opener.postMessage({ type: 'invoice-management-download-pdf', invoiceId: '${escapeHtml(invoice.id)}' }, window.location.origin)">Download Invoice as PDF</button></div>`
    }
    <div class="red-bar"></div>
    <div class="title-row">
      <div>
        <h1>TAX INVOICE NO- ${escapeHtml(invoice.invoiceNumber)}</h1>
        <div class="company-block">${escapeHtml(companyHeaderDetails)}</div>
      </div>
      <div class="invoice-meta">
        <div class="date">${escapeHtml(formatDate(invoice.invoiceDate))}</div>
        <div class="number">INVOICE NO ${escapeHtml(invoice.invoiceNumber)}</div>
      </div>
    </div>

    <div class="party-row">
      <div>
        <div class="section-label">BILL TO</div>
        <div class="address-block">${escapeHtml(billToDetails)}</div>
      </div>
      <div>
        <div class="section-label">SHIP TO</div>
        <div class="address-block">${escapeHtml(shipToDetails)}</div>
      </div>
    </div>

    <table class="line-items">
      <thead>
        <tr>
          <th>DESCRIPTION</th>
          <th>Days</th>
          <th>HSN CODE</th>
          <th>Month</th>
          <th>TOTAL</th>
        </tr>
      </thead>
      <tbody>${resourceRows}${fillerRows}</tbody>
    </table>

    <div class="subtotal-row">
      <div>Remarks / Payment Instructions:</div>
      <div class="subtotal-box">
        <div>SUBTOTAL</div>
        <div>${escapeHtml(formatMoney(invoice.subtotal))}</div>
      </div>
    </div>

    <div class="tax-area">
      ${buildTaxTableMarkup(gstBreakdown, invoice.hsnCode)}
      <div class="tax-total">
        <div class="label">Total Tax Amount</div>
        <div class="value">${escapeHtml(formatMoney(gstBreakdown.totalTax))}</div>
      </div>
    </div>

    <div class="payment-section">
      <div class="payment-left">
        <div class="company-tax">${escapeHtml(companyTaxDetails)}</div>
        <div class="bank">${escapeHtml(invoice.bankDetails)}</div>
        <div class="bank-label">Bank account Details</div>
      </div>
      <div class="grand-total"></div>
      <div class="total-footer-row">
        <div></div>
        <div>Total</div>
        <div class="words-value">${escapeHtml(totalInWords)}</div>
        <div>${escapeHtml(formatMoney(invoice.grandTotal))}</div>
      </div>
    </div>

    <div class="signature">
      <strong>For ${escapeHtml(invoice.companyName || "Company")},</strong>
      ${invoice.signatureDataUrl ? `<img src="${escapeHtml(invoice.signatureDataUrl)}" alt="Authorised Signatory" />` : "<div style=\"height: 54px;\"></div>"}
      <div>Authorised Signatory</div>
    </div>
    <div class="bottom-bar"></div>
  </div>
  ${autoPrint
      ? `<script>
          window.addEventListener("load", function () {
            setTimeout(function () {
              window.focus();
              window.print();
            }, 250);
          });
        </script>`
      : ""
    }
</body>
</html>`;
};

const InvoiceManagementPage = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth.auth);
  const { allLeaves = [] } = useSelector((state) => state.leaveAprrove || {});
  const { employees } = useSelector((state) => state.employee);
  const [form, setForm] = useState(initialForm);
  const [resourceDraft, setResourceDraft] = useState(initialResource);
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [leaveRecords, setLeaveRecords] = useState([]);
  const [errors, setErrors] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [isInvoiceNumberEdited, setIsInvoiceNumberEdited] = useState(false);
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [invoiceNumberFilter, setInvoiceNumberFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [showSendMailModal, setShowSendMailModal] = useState(false);
  const [invoiceToSendMail, setInvoiceToSendMail] = useState(null);
  const [invoiceMailDraft, setInvoiceMailDraft] = useState({ subject: "", body: "", cc: "", bcc: "" });
  const [sendingInvoiceMail, setSendingInvoiceMail] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [timesheetToDetach, setTimesheetToDetach] = useState(null);
  const [timesheetLoadingId, setTimesheetLoadingId] = useState("");
  const invoiceMailDraftRef = useRef({ subject: "", body: "", cc: "", bcc: "" });
  const invoiceMonthInputRef = useRef(null);

  const adminName = useMemo(() => {
    const name = [user?.firstname, user?.lastname].filter(Boolean).join(" ").trim();
    return name || user?.email || "Admin";
  }, [user]);

  useEffect(() => {
    invoiceMailDraftRef.current = invoiceMailDraft;
  }, [invoiceMailDraft]);

  const canCreateInvoice =
    hasAdminAccess(user) || checkPermission(permissionType.INVOICE_MANAGEMENT, "create");
  const canUpdateInvoice =
    hasAdminAccess(user) || checkPermission(permissionType.INVOICE_MANAGEMENT, "update");
  const canDeleteInvoice =
    hasAdminAccess(user) || checkPermission(permissionType.INVOICE_MANAGEMENT, "delete");
  const canSendInvoiceMail =
    hasAdminAccess(user) || checkPermission(permissionType.INVOICE_MANAGEMENT, "send mail");

  const loadBillingData = async () => {
    setLoading(true);
    try {
      const [invoiceList, clientList, companyList] = await Promise.all([
        fetchInvoices(),
        fetchClientProfiles(),
        fetchCompanyProfiles(),
      ]);
      setInvoices(invoiceList);
      setClients(clientList);
      setCompanies(companyList);
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Unable to load invoice data.");
      setInvoices([]);
      setClients([]);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBillingData();
    dispatch(getEmployeesAPI({ page: 1, limit: 500, filterBy: "active" }));
    const onCompaniesUpdated = () => {
      fetchCompanyProfiles()
        .then(setCompanies)
        .catch(() => { });
    };
    window.addEventListener(COMPANY_PROFILES_UPDATED_EVENT, onCompaniesUpdated);
    return () => window.removeEventListener(COMPANY_PROFILES_UPDATED_EVENT, onCompaniesUpdated);
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAllLeavesAPI({ page: 1, limit: 1000, status: "approved" }))
      .unwrap()
      .then((response) => {
        const approvedLeaves = response?.data?.leaveRequests;
        if (Array.isArray(approvedLeaves)) setLeaveRecords(approvedLeaves);
      })
      .catch(() => {
        setLeaveRecords([]);
      });
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(allLeaves) && allLeaves.length) {
      setLeaveRecords(allLeaves);
    }
  }, [allLeaves]);

  const clientOptions = useMemo(
    () =>
      clients.map((client, index) => ({
        value: getRecordId(client, index, "client"),
        label: getClientLabel(client),
      })),
    [clients]
  );

  const companyOptions = useMemo(
    () =>
      companies.map((company, index) => ({
        value: getRecordId(company, index, "company"),
        label: getCompanyLabel(company),
      })),
    [companies]
  );

  const employeeOptions = useMemo(
    () =>
      (Array.isArray(employees) ? employees : [])
        .filter((employee) => Number(employee?.roleId) !== 1)
        .map((employee) => ({
          value: String(employee.userId || employee.id),
          label: `${employee.firstname || ""} ${employee.lastname || ""}`.trim() || employee.email,
        }))
        .filter((option) => option.value && option.label),
    [employees]
  );

  const selectedClient = useMemo(
    () => clients.find((client, index) => getRecordId(client, index, "client") === form.clientId),
    [clients, form.clientId]
  );

  const selectedCompany = useMemo(
    () => companies.find((company, index) => getRecordId(company, index, "company") === form.companyId),
    [companies, form.companyId]
  );

  const totals = useMemo(
    () => calculateTotals(form.resources, form.gstPercentage),
    [form.resources, form.gstPercentage]
  );

  const formGstBreakdown = useMemo(
    () =>
      getInvoiceGstBreakdown({
        gstPercentage: form.gstPercentage,
        gst: totals.gst,
        subtotal: totals.subtotal,
        clientAddress: selectedClient?.companyAddress || "",
        clientGstNumber: selectedClient?.gstNumber || "",
      }),
    [form.gstPercentage, selectedClient, totals.gst, totals.subtotal]
  );

  const invoiceNumberFilterOptions = useMemo(
    () =>
      Array.from(new Set(invoices.map((invoice) => invoice.invoiceNumber).filter(Boolean))).map((invoiceNumber) => ({
        value: invoiceNumber,
        label: invoiceNumber,
      })),
    [invoices]
  );

  const invoiceClientFilterOptions = useMemo(
    () =>
      Array.from(new Set(invoices.map((invoice) => invoice.clientName).filter(Boolean))).map((clientName) => ({
        value: clientName,
        label: clientName,
      })),
    [invoices]
  );

  const invoiceCompanyFilterOptions = useMemo(
    () =>
      Array.from(new Set(invoices.map((invoice) => invoice.companyName).filter(Boolean))).map((companyName) => ({
        value: companyName,
        label: companyName,
      })),
    [invoices]
  );

  const filteredInvoices = useMemo(() => {
    const query = invoiceSearch.trim().toLowerCase();
    return invoices.filter((invoice) => {
      const matchesSearch =
        !query ||
        [
          invoice.invoiceNumber,
          invoice.clientName,
          invoice.clientCompanyName,
          invoice.companyName,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));

      return (
        matchesSearch &&
        (!invoiceNumberFilter || invoice.invoiceNumber === invoiceNumberFilter) &&
        (!clientFilter || invoice.clientName === clientFilter) &&
        (!companyFilter || invoice.companyName === companyFilter)
      );
    });
  }, [invoices, invoiceSearch, invoiceNumberFilter, clientFilter, companyFilter]);

  const recalculateResourceDays = (resources, invoiceMonth = form.invoiceMonth) =>
    applyCalculatedResourceDays(resources, invoiceMonth, leaveRecords);

  useEffect(() => {
    if (!showForm || !form.invoiceMonth || !form.resources.length) return;
    setForm((current) => ({
      ...current,
      resources: recalculateResourceDays(current.resources, current.invoiceMonth),
    }));
  }, [leaveRecords]);

  const resetForm = () => {
    setForm(initialForm);
    setResourceDraft(initialResource);
    setErrors({});
    setEditingInvoiceId(null);
    setIsInvoiceNumberEdited(false);
    setShowForm(false);
  };

  const openCreateForm = () => {
    if (!canCreateInvoice) {
      showErrorToast("You do not have permission to create invoices.");
      return;
    }
    const invoiceMonth = getCurrentMonthInputValue();
    setForm({
      ...initialForm,
      invoiceNumber: "",
      invoiceDate: getTodayInputValue(),
      invoiceMonth,
    });
    setResourceDraft(initialResource);
    setErrors({});
    setEditingInvoiceId(null);
    setIsInvoiceNumberEdited(false);
    setShowForm(true);
  };

  const openEditForm = (invoice) => {
    if (!canUpdateInvoice) {
      showErrorToast("You do not have permission to update invoices.");
      return;
    }
    setForm({
      ...initialForm,
      ...invoice,
      resources: normalizeResources(invoice.resources),
      attachedTimesheets: Array.isArray(invoice.attachedTimesheets) ? invoice.attachedTimesheets : [],
    });
    setResourceDraft(initialResource);
    setErrors({});
    setEditingInvoiceId(invoice.id);
    setIsInvoiceNumberEdited(true);
    setShowForm(true);
  };

  const updateField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: "" }));
  };

  const openInvoiceMonthPicker = () => {
    const input = invoiceMonthInputRef.current;
    if (input?.showPicker) {
      try {
        input.showPicker();
      } catch {
        input.focus();
      }
      return;
    }
    input?.focus();
  };

  const handleClientChange = (clientId) => {
    if (!clientId) {
      setForm((current) => ({
        ...current,
        clientId: "",
        clientDetails: "",
        resources: [],
      }));
      setErrors((current) => ({ ...current, clientId: "", resources: "" }));
      return;
    }

    const client = clients.find((item, index) => getRecordId(item, index, "client") === clientId);
    const linkedCompanyId = client?.onboardedCompanyId ? String(client.onboardedCompanyId) : "";
    const linkedCompany =
      companies.find((item, index) => getRecordId(item, index, "company") === linkedCompanyId) ||
      companies.find((item) => getCompanyLabel(item) === client?.companyName);

    setForm((current) => {
      const nextCompanyId = linkedCompany
        ? getRecordId(linkedCompany, companies.indexOf(linkedCompany), "company")
        : current.companyId;

      return {
        ...current,
        clientId,
        companyId: nextCompanyId,
        gstNumber: client?.gstNumber || linkedCompany?.gstNumber || current.gstNumber,
        hsnCode: client?.hsnCode || linkedCompany?.hsnCode || current.hsnCode,
        companyAddress: linkedCompany?.companyAddress || client?.companyAddress || current.companyAddress,
        clientDetails: buildClientDetails(client),
        companyDetails: linkedCompany ? buildCompanyDetails(linkedCompany) : current.companyDetails,
        bankDetails: linkedCompany ? buildBankDetails(linkedCompany) : current.bankDetails,
        resources: recalculateResourceDays(normalizeResources(client?.resources), current.invoiceMonth),
        attachedTimesheets: [],
        invoiceNumber: resolveAutoInvoiceNumber({
          editingInvoiceId,
          isInvoiceNumberEdited,
          invoices,
          companyId: nextCompanyId,
          currentNumber: current.invoiceNumber,
        }),
      };
    });
    setErrors((current) => ({ ...current, clientId: "", resources: "" }));
  };

  const handleCompanyChange = (companyId) => {
    if (!companyId) {
      setForm((current) => ({
        ...current,
        companyId: "",
        companyDetails: "",
        bankDetails: "",
        companyAddress: "",
      }));
      setErrors((current) => ({ ...current, companyId: "" }));
      return;
    }

    const company = companies.find((item, index) => getRecordId(item, index, "company") === companyId);
    setForm((current) => ({
      ...current,
      companyId,
      gstNumber: company?.gstNumber || current.gstNumber,
      hsnCode: company?.hsnCode || current.hsnCode,
      companyAddress: company?.companyAddress || current.companyAddress,
      companyDetails: buildCompanyDetails(company),
      bankDetails: buildBankDetails(company),
      invoiceNumber: resolveAutoInvoiceNumber({
        editingInvoiceId,
        isInvoiceNumberEdited,
        invoices,
        companyId,
        currentNumber: current.invoiceNumber,
      }),
    }));
    setErrors((current) => ({ ...current, companyId: "" }));
  };

  const updateResource = (resourceId, key, value) => {
    setForm((current) => ({
      ...current,
      resources: current.resources.map((resource) =>
        resource.id === resourceId
          ? (() => {
            const nextResource = { ...resource, [key]: value };
            if (key === "price" || key === "quantity") {
              const workingDays = Number(nextResource.workingDays) || getWorkingDaysInMonth(current.invoiceMonth);
              nextResource.totalAmount = calculateAmountForDays(
                nextResource.price,
                nextResource.quantity,
                workingDays
              );
            }
            return nextResource;
          })()
          : resource
      ),
    }));
    setErrors((current) => ({ ...current, resources: "" }));
  };

  const addResource = () => {
    const price = Number(resourceDraft.price);
    const quantity = Number(resourceDraft.quantity);
    const selectedEmployee = employeeOptions.find((option) => option.value === String(resourceDraft.employeeId || ""));
    const employeeName = selectedEmployee?.label || resourceDraft.employeeName;

    if (!String(employeeName || "").trim()) {
      showErrorToast("Employee Name is required.");
      return;
    }
    if (resourceDraft.price === "" || Number.isNaN(price) || price < 0) {
      showErrorToast("Resource Price must be a positive numeric value.");
      return;
    }
    if (resourceDraft.quantity === "" || Number.isNaN(quantity) || quantity < 0) {
      showErrorToast("Days cannot be negative.");
      return;
    }

    setForm((current) => ({
      ...current,
      resources: [
        ...current.resources,
        (() => {
          const workingDays = getWorkingDaysInMonth(current.invoiceMonth);
          return {
            id: `manual-${Date.now()}`,
            employeeId: resourceDraft.employeeId ? String(resourceDraft.employeeId) : "",
            employeeName: employeeName.trim(),
            price,
            note: String(resourceDraft.note || "").trim(),
            totalAmount: calculateAmountForDays(price, quantity, workingDays),
            quantity,
            workingDays,
            leaveDays: Math.max(0, workingDays - quantity),
            calculatedDays: quantity,
          };
        })(),
      ],
    }));
    setResourceDraft(initialResource);
    setErrors((current) => ({ ...current, resources: "" }));
  };

  const removeResource = (resourceId) => {
    setForm((current) => {
      const removedResource = current.resources.find((resource) => resource.id === resourceId);
      const removedEmployeeId = String(removedResource?.employeeId || "");
      return {
        ...current,
        resources: current.resources.filter((resource) => resource.id !== resourceId),
        attachedTimesheets: removedEmployeeId
          ? current.attachedTimesheets.filter((entry) => entry.employeeId !== removedEmployeeId)
          : current.attachedTimesheets,
      };
    });
  };

  const fetchEmployeeSpreadsheetUrl = async (employeeId) => {
    const response = await protectedApi({
      url: `${APIS.TASK_EMPLOYEE_SPREADSHEET}/${employeeId}`,
      method: "GET",
    });
    const url = response?.data?.data?.url;
    if (!url) {
      throw new Error("Google Sheet is not available for this employee.");
    }
    return url;
  };

  const getAttachedTimesheet = (employeeId) =>
    form.attachedTimesheets.find((entry) => String(entry.employeeId) === String(employeeId));

  const viewResourceTimesheet = async (resource) => {
    if (!resource?.employeeId) {
      showErrorToast("Link this resource to an employee to view the timesheet.");
      return;
    }

    setTimesheetLoadingId(String(resource.employeeId));
    try {
      const url = await fetchEmployeeSpreadsheetUrl(resource.employeeId);
      window.open(url, "_blank", "noopener,noreferrer");
      showSuccessToast(
        `Opened ${resource.employeeName}'s timesheet. Update the ${formatMonth(form.invoiceMonth)} tab if needed, then tap Attach.`
      );
    } catch (err) {
      const message =
        err?.response?.data?.error?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Unable to open timesheet.";
      showErrorToast(typeof message === "string" ? message : "Unable to open timesheet.");
    } finally {
      setTimesheetLoadingId("");
    }
  };

  const attachResourceTimesheet = async (resource) => {
    if (!resource?.employeeId) {
      showErrorToast("Link this resource to an employee to attach the timesheet.");
      return;
    }

    setTimesheetLoadingId(String(resource.employeeId));
    try {
      await fetchEmployeeSpreadsheetUrl(resource.employeeId);
      setForm((current) => ({
        ...current,
        attachedTimesheets: [
          ...current.attachedTimesheets.filter(
            (entry) => String(entry.employeeId) !== String(resource.employeeId)
          ),
          {
            employeeId: String(resource.employeeId),
            employeeName: resource.employeeName,
            month: current.invoiceMonth,
            sheetTab: formatMonth(current.invoiceMonth),
          },
        ],
      }));
      showSuccessToast(`Timesheet attached for ${resource.employeeName}. It will be sent as a PDF with the invoice email.`);
    } catch (err) {
      const message =
        err?.response?.data?.error?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Unable to attach timesheet.";
      showErrorToast(typeof message === "string" ? message : "Unable to attach timesheet.");
    } finally {
      setTimesheetLoadingId("");
    }
  };

  const detachResourceTimesheet = (employeeId) => {
    setForm((current) => ({
      ...current,
      attachedTimesheets: current.attachedTimesheets.filter(
        (entry) => String(entry.employeeId) !== String(employeeId)
      ),
    }));
  };

  const requestDetachResourceTimesheet = (resource) => {
    setTimesheetToDetach({
      employeeId: resource.employeeId,
      employeeName: resource.employeeName,
    });
  };

  const confirmDetachResourceTimesheet = () => {
    if (!timesheetToDetach?.employeeId) return;
    detachResourceTimesheet(timesheetToDetach.employeeId);
    setTimesheetToDetach(null);
    showSuccessToast(`Timesheet detached for ${timesheetToDetach.employeeName || "employee"}.`);
  };

  const openSendMailDialog = (invoice) => {
    const hasTimesheets = (invoice?.attachedTimesheets || []).length > 0;
    const senderName = invoice?.companyOwnerName || adminName;
    const client = getInvoiceClientRecord(invoice);
    setInvoiceToSendMail(invoice);
    setInvoiceMailDraft({
      subject: buildInvoiceMailSubject({ invoice, hasTimesheets }),
      body: buildInvoiceMailPlainText({
        invoice,
        senderName,
        senderTitle: "Accounts Head.",
        hasTimesheets,
      }),
      cc: client?.ccEmail || invoice?.clientCcEmail || "",
      bcc: client?.bccEmail || invoice?.clientBccEmail || "",
    });
    setShowSendMailModal(true);
  };

  const handleSendInvoiceMail = async () => {
    if (!invoiceToSendMail || sendingInvoiceMail) return;
    if (!canSendInvoiceMail) {
      showErrorToast("You do not have permission to send invoice emails.");
      return;
    }

    const clientEmail = getInvoiceClientEmail(invoiceToSendMail);
    if (!clientEmail) {
      showErrorToast("Client email is required to send invoice. Add it in Client Management.");
      return;
    }
    const mailDraft = invoiceMailDraftRef.current;
    if (!mailDraft.subject.trim() || !mailDraft.body.trim()) {
      showErrorToast("Email subject and message are required.");
      return;
    }
    if (!isValidEmailList(mailDraft.cc)) {
      showErrorToast("Enter valid CC emails separated by commas.");
      return;
    }
    if (!isValidEmailList(mailDraft.bcc)) {
      showErrorToast("Enter valid BCC emails separated by commas.");
      return;
    }

    setSendingInvoiceMail(true);
    try {
      const { blob: invoiceBlob, fileName } = await generateInvoicePdf(invoiceToSendMail, { forEmail: true });
      const attachedTimesheets = invoiceToSendMail.attachedTimesheets || [];
      const timesheetPdfs = [];

      for (const entry of attachedTimesheets) {
        const pdf = await fetchEmployeeTimesheetPdf(entry.employeeId, invoiceToSendMail.invoiceMonth);
        timesheetPdfs.push(pdf);
      }

      await sendInvoiceEmail({
        pdfBlob: invoiceBlob,
        pdfFileName: fileName,
        timesheetPdfs,
        to: clientEmail,
        subject: mailDraft.subject,
        body: mailDraft.body,
        emailSubject: mailDraft.subject,
        emailBody: mailDraft.body,
        cc: mailDraft.cc,
        bcc: mailDraft.bcc,
        sentBy: adminName,
        invoice: {
          invoiceNumber: invoiceToSendMail.invoiceNumber,
          clientName: invoiceToSendMail.clientName,
          invoiceMonth: invoiceToSendMail.invoiceMonth,
        },
        senderName: invoiceToSendMail.companyOwnerName || adminName,
        senderTitle: "Accounts Head.",
        attachedTimesheets,
        replyTo: invoiceToSendMail.companyEmail || undefined,
      });
      showSuccessToast(
        "Invoice email sent to the client. Himanshu and Rishi have been notified."
      );
      setShowSendMailModal(false);
      setInvoiceToSendMail(null);
      setInvoiceMailDraft({ subject: "", body: "", cc: "", bcc: "" });
    } catch (err) {
      const status = err?.response?.status;
      const message =
        err?.response?.data?.error?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Unable to send invoice email.";
      if (status === 413) {
        showErrorToast("Attachment size is too large to send. Try with fewer timesheets.");
      } else {
        showErrorToast(typeof message === "string" ? message : "Unable to send invoice email.");
      }
    } finally {
      setSendingInvoiceMail(false);
    }
  };

  const getInvoiceMailPreview = (invoice) => {
    if (!invoice) return null;
    const hasTimesheets = (invoice.attachedTimesheets || []).length > 0;
    return {
      to: getInvoiceClientEmail(invoice) || "—",
      cc: invoiceMailDraft.cc || "—",
      bcc: invoiceMailDraft.bcc || "—",
      subject: invoiceMailDraft.subject || buildInvoiceMailSubject({ invoice, hasTimesheets }),
      attachments: getInvoiceMailAttachmentNames(invoice, invoice.attachedTimesheets || []),
    };
  };

  const getInvoiceClientRecord = (invoice) => {
    if (!invoice) return "";
    return clients.find(
      (item, index) => getRecordId(item, index, "client") === invoice.clientId
    );
  };

  const getInvoiceClientEmail = (invoice) => {
    if (!invoice) return "";
    const client = getInvoiceClientRecord(invoice);
    return client?.emailId || invoice.clientEmail || "";
  };

  const getInvoiceActionMenuItems = (invoice) => {
    const items = [
      { key: "preview", label: "Preview" },
      { key: "download", label: "Download PDF" },
    ];

    if (canSendInvoiceMail) {
      items.push({ key: "send-mail", label: "Send Mail" });
    }

    if (canUpdateInvoice) {
      items.push({ key: "edit", label: "Edit" });
    }
    if (canDeleteInvoice) {
      items.push({ type: "divider" });
      items.push({ key: "delete", label: "Delete", danger: true });
    }

    return items;
  };

  const handleInvoiceAction = (key, invoice) => {
    switch (key) {
      case "preview":
        previewInvoice(invoice);
        break;
      case "download":
        downloadInvoice(invoice);
        break;
      case "send-mail":
        openSendMailDialog(invoice);
        break;
      case "edit":
        openEditForm(invoice);
        break;
      case "delete":
        setInvoiceToDelete(invoice);
        break;
      default:
        break;
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!/^\d{4}-\d{2}$/.test(form.invoiceMonth)) {
      nextErrors.invoiceMonth = "Invoice Month must be a valid month/year.";
    }
    if (!form.invoiceDate || Number.isNaN(new Date(form.invoiceDate).getTime())) {
      nextErrors.invoiceDate = "Invoice Date must be a valid date.";
    }
    if (!form.clientId) {
      nextErrors.clientId = "Select Client is required.";
    }
    if (!form.companyId) {
      nextErrors.companyId = "Select Company is required.";
    }
    if (!String(form.gstNumber || "").trim()) {
      nextErrors.gstNumber = "GST Number is required.";
    }
    if (!String(form.hsnCode || "").trim()) {
      nextErrors.hsnCode = "HSN Code is required.";
    }
    if (!Array.isArray(form.resources) || form.resources.length === 0) {
      nextErrors.resources = "No resources assigned to selected client.";
    }
    if (form.gstPercentage === "" || Number.isNaN(Number(form.gstPercentage)) || Number(form.gstPercentage) < 0) {
      nextErrors.gstPercentage = "GST percentage must be a positive numeric value.";
    }

    form.resources.forEach((resource, index) => {
      const price = Number(resource.price);
      const totalAmount = Number(resource.totalAmount);
      const quantity = Number(resource.quantity);
      if (!resource.employeeName || resource.employeeName.trim() === "") {
        nextErrors.resources = `Resource ${index + 1}: Employee Name is required.`;
      } else if (Number.isNaN(price) || price < 0) {
        nextErrors.resources = `Resource ${index + 1}: Price must be a positive numeric value.`;
      } else if (Number.isNaN(totalAmount) || totalAmount < 0) {
        nextErrors.resources = `Resource ${index + 1}: Total Amount must be a positive numeric value.`;
      } else if (Number.isNaN(quantity) || quantity < 0) {
        nextErrors.resources = `Resource ${index + 1}: Days cannot be negative.`;
      }
    });

    const duplicateNumber = invoices.some(
      (invoice) =>
        invoice.invoiceNumber === form.invoiceNumber &&
        String(invoice.companyId || "") === String(form.companyId || "") &&
        invoice.id !== editingInvoiceId
    );
    if (duplicateNumber) {
      nextErrors.invoiceNumber = "Invoice number must be unique for this company.";
    }

    setErrors(nextErrors);
    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!editingInvoiceId && !canCreateInvoice) {
      showErrorToast("You do not have permission to create invoices.");
      return;
    }
    if (editingInvoiceId && !canUpdateInvoice) {
      showErrorToast("You do not have permission to update invoices.");
      return;
    }

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length) {
      showErrorToast(Object.values(validationErrors)[0]);
      return;
    }

    const invoiceNumber =
      form.invoiceNumber || generateInvoiceNumber(invoices, form.companyId);
    const invoice = {
      ...form,
      invoiceNumber,
      clientId: form.clientId,
      companyId: form.companyId,
      clientName: getClientLabel(selectedClient),
      clientCompanyName: selectedClient?.companyName || getClientLabel(selectedClient),
      clientAddress: selectedClient?.companyAddress || "",
      clientEmail: selectedClient?.emailId || "",
      clientCcEmail: selectedClient?.ccEmail || "",
      clientBccEmail: selectedClient?.bccEmail || "",
      companyName: getCompanyLabel(selectedCompany),
      companyOwnerName: selectedCompany?.ownerName || "",
      companyAddress: selectedCompany?.companyAddress || form.companyAddress,
      companyPhone: selectedCompany?.contactNumber || "",
      companyEmail: selectedCompany?.emailId || "",
      clientGstNumber: selectedClient?.gstNumber || form.gstNumber,
      companyGstNumber: selectedCompany?.gstNumber || form.gstNumber,
      companyPanNumber: selectedCompany?.panNumber || "",
      resources: form.resources.map((resource) => ({
        ...resource,
        price: Number(resource.price),
        totalAmount: getResourceTotalAmount(resource),
        quantity: Number(resource.quantity),
      })),
      gstPercentage: Number(form.gstPercentage),
      attachedTimesheets: Array.isArray(form.attachedTimesheets) ? form.attachedTimesheets : [],
      ...calculateTotals(form.resources, form.gstPercentage),
    };

    setSaving(true);
    try {
      if (editingInvoiceId) {
        await updateInvoiceApi(editingInvoiceId, invoice);
        showSuccessToast("Invoice updated successfully. PDF can be regenerated.");
      } else {
        await createInvoiceApi(invoice);
        showSuccessToast("Invoice created successfully. PDF generated successfully.");
      }
      await loadBillingData();
      resetForm();
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Unable to save invoice.");
    } finally {
      setSaving(false);
    }
  };

  const getPrintableInvoice = (invoice) => {
    const company = companies.find((item, index) => getRecordId(item, index, "company") === invoice.companyId);
    const client = clients.find((item, index) => getRecordId(item, index, "client") === invoice.clientId);
    return {
      ...invoice,
      signatureDataUrl: company?.signatureDataUrl || "",
      clientAddress: invoice.clientAddress || client?.companyAddress || "",
      clientGstNumber: invoice.clientGstNumber || client?.gstNumber || "",
      gstPercentage: Number(invoice.gstPercentage) || 0,
      gst: Number(invoice.gst) || 0,
      subtotal: Number(invoice.subtotal) || 0,
    };
  };

  const previewInvoice = (invoice) => {
    const previewWindow = window.open("", "_blank");
    if (!previewWindow) {
      showErrorToast("Popup blocked. Please allow popups to preview invoice.");
      return;
    }
    previewWindow.document.write(buildInvoiceHtml(getPrintableInvoice(invoice)));
    previewWindow.document.close();
  };

  const generateInvoicePdf = async (invoice, options = {}) => {
    const { forEmail = false } = options;
    const printableInvoice = getPrintableInvoice(invoice);
    const frame = document.createElement("iframe");
    frame.style.position = "fixed";
    frame.style.left = "-10000px";
    frame.style.top = "0";
    frame.style.width = "1120px";
    frame.style.height = "900px";
    document.body.appendChild(frame);

    try {
      const frameDocument = frame.contentDocument || frame.contentWindow?.document;
      if (!frameDocument) throw new Error("Unable to create invoice document.");

      frameDocument.open();
      frameDocument.write(buildInvoiceHtml(printableInvoice, { hideActions: true }));
      frameDocument.close();

      await new Promise((resolve) => {
        frame.onload = resolve;
        setTimeout(resolve, 500);
      });
      await frame.contentWindow?.document?.fonts?.ready;

      const invoiceSheet = frameDocument.querySelector(".invoice-sheet");
      if (!invoiceSheet) throw new Error("Invoice template not found.");

      const canvas = await html2canvas(invoiceSheet, {
        backgroundColor: "#ffffff",
        scale: forEmail ? 1.25 : 2,
        useCORS: true,
        foreignObjectRendering: true,
        windowWidth: 1120,
        windowHeight: invoiceSheet.scrollHeight,
      });
      const imageFormat = forEmail ? "JPEG" : "PNG";
      const imageData = forEmail
        ? canvas.toDataURL("image/jpeg", 0.82)
        : canvas.toDataURL("image/png");
      const pdf = new jsPDF("landscape", "pt", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const pagePadding = 16;
      const ratio = Math.min((pageWidth - pagePadding * 2) / canvas.width, pageHeight / canvas.height) * 0.98;
      const imageWidth = canvas.width * ratio;
      const imageHeight = canvas.height * ratio;
      const x = (pageWidth - imageWidth) / 2;
      const y = 0;

      pdf.addImage(imageData, imageFormat, x, y, imageWidth, imageHeight, undefined, forEmail ? "FAST" : undefined);
      const fileName = `${printableInvoice.invoiceNumber || "invoice"}.pdf`;
      return { blob: pdf.output("blob"), fileName, pdf };
    } finally {
      document.body.removeChild(frame);
    }
  };

  const downloadInvoice = async (invoice) => {
    try {
      const { pdf, fileName } = await generateInvoicePdf(invoice);
      pdf.save(fileName);
      showSuccessToast("Invoice PDF downloaded successfully.");
    } catch {
      showErrorToast("Unable to download invoice PDF. Please try again.");
    }
  };

  const confirmDeleteInvoice = async () => {
    if (!invoiceToDelete) return;
    try {
      await deleteInvoiceApi(invoiceToDelete.id);
      setInvoiceToDelete(null);
      await loadBillingData();
      showSuccessToast("Invoice deleted successfully.");
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Unable to delete invoice.");
    }
  };

  useEffect(() => {
    const handlePreviewDownload = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "invoice-management-download-pdf") return;

      const invoice = invoices.find((item) => String(item.id) === String(event.data.invoiceId));
      if (invoice) {
        downloadInvoice(invoice);
      }
    };

    window.addEventListener("message", handlePreviewDownload);
    return () => window.removeEventListener("message", handlePreviewDownload);
  }, [invoices, companies]);

  return (
    <div className="invoice-management-page">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="page-title">Invoice Management</h1>
          <p className="page-subtitle">
            Create invoices from client, company, and resource allocation data with editable pricing.
          </p>
        </div>
        {canCreateInvoice && !showForm ? (
          <button
            type="button"
            onClick={openCreateForm}
            className="button bg-btn-primary text-white px-6 py-2 rounded-lg self-start"
          >
            Create Invoice
          </button>
        ) : null}
      </div>

      {showForm ? (
        <div className="relative mb-6">
          {saving ? (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/75"
              aria-live="polite"
              aria-busy="true"
            >
              <Loader className="!h-auto" />
            </div>
          ) : null}
          <form onSubmit={handleSubmit} className="card-border card-body">
            <div className="flex flex-col gap-1 mb-5">
              <h2 className="text-lg font-semibold text-[#27314a]">
                {editingInvoiceId ? "Update Invoice" : "Create Invoice"}
              </h2>
              <p className="text-sm text-[#6f7d97]">
                Select month, client, and company to auto-fetch GST, HSN, bank, and resource details.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="flex flex-col gap-1">
                <span className="text-sm font-medium text-[#27314a]">Invoice Number</span>
                <input
                  className="search-input"
                  value={form.invoiceNumber}
                  onChange={(event) => {
                    setIsInvoiceNumberEdited(true);
                    updateField("invoiceNumber", event.target.value.toUpperCase());
                  }}
                  placeholder="01"
                />
                {errors.invoiceNumber ? <span className="text-red-500 text-xs">{errors.invoiceNumber}</span> : null}
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm font-medium text-[#27314a]">Invoice Date</span>
                <input
                  type="date"
                  className="search-input"
                  value={form.invoiceDate}
                  onChange={(event) => updateField("invoiceDate", event.target.value)}
                />
                {errors.invoiceDate ? <span className="text-red-500 text-xs">{errors.invoiceDate}</span> : null}
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm font-medium text-[#27314a]">Invoice Month</span>
                <input
                  ref={invoiceMonthInputRef}
                  type="month"
                  className="search-input"
                  value={form.invoiceMonth}
                  onClick={openInvoiceMonthPicker}
                  onChange={(event) => {
                    const invoiceMonth = event.target.value;
                    setForm((current) => ({
                      ...current,
                      invoiceMonth,
                      resources: recalculateResourceDays(current.resources, invoiceMonth),
                      attachedTimesheets: [],
                    }));
                    setErrors((current) => ({ ...current, invoiceMonth: "" }));
                  }}
                />
                {errors.invoiceMonth ? <span className="text-red-500 text-xs">{errors.invoiceMonth}</span> : null}
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <label className="flex flex-col gap-1">
                <span className="text-sm font-medium text-[#27314a]">Select Client</span>
                <Select
                  value={form.clientId || undefined}
                  onChange={handleClientChange}
                  options={clientOptions}
                  placeholder="Select client"
                  className="w-full"
                  showSearch
                  allowClear
                  optionFilterProp="label"
                />
                {errors.clientId ? <span className="text-red-500 text-xs">{errors.clientId}</span> : null}
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm font-medium text-[#27314a]">Select Company</span>
                <Select
                  value={form.companyId || undefined}
                  onChange={handleCompanyChange}
                  options={companyOptions}
                  placeholder="Select company"
                  className="w-full"
                  showSearch
                  allowClear
                  optionFilterProp="label"
                />
                {errors.companyId ? <span className="text-red-500 text-xs">{errors.companyId}</span> : null}
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <label className="flex flex-col gap-1">
                <span className="text-sm font-medium text-[#27314a]">GST Number</span>
                <input
                  className="search-input"
                  value={form.gstNumber}
                  onChange={(event) => updateField("gstNumber", event.target.value.toUpperCase())}
                />
                {errors.gstNumber ? <span className="text-red-500 text-xs">{errors.gstNumber}</span> : null}
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm font-medium text-[#27314a]">HSN Code</span>
                <input
                  className="search-input"
                  value={form.hsnCode}
                  onChange={(event) => updateField("hsnCode", event.target.value)}
                />
                {errors.hsnCode ? <span className="text-red-500 text-xs">{errors.hsnCode}</span> : null}
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm font-medium text-[#27314a]">GST Percentage</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="search-input"
                  value={form.gstPercentage}
                  onChange={(event) => updateField("gstPercentage", event.target.value)}
                />
                {errors.gstPercentage ? <span className="text-red-500 text-xs">{errors.gstPercentage}</span> : null}
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {[
                ["companyDetails", "Company Details"],
                ["clientDetails", "Client Details"],
                ["companyAddress", "Company Address"],
                ["bankDetails", "Bank Details"],
                ["templateDetails", "Invoice Template Details"],
              ].map(([key, label]) => (
                <label key={key} className="flex flex-col gap-1">
                  <span className="text-sm font-medium text-[#27314a]">{label}</span>
                  <textarea
                    className="search-input resize-none overflow-hidden"
                    rows={getDetailRows(form[key])}
                    value={form[key]}
                    onChange={(event) => updateField(key, event.target.value)}
                  />
                </label>
              ))}
            </div>

            <div className="mt-6">
              <div className="flex flex-col gap-3 mb-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex flex-col gap-1">
                  <h3 className="text-base font-semibold text-[#27314a]">Resources</h3>
                  <p className="text-sm text-[#6f7d97]">
                    Days are calculated from month working days minus approved leave. You can edit days if needed.
                  </p>
                  {errors.resources ? <span className="text-red-500 text-xs">{errors.resources}</span> : null}
                </div>
                <button
                  type="button"
                  className="button border border-[#d7deed] bg-white text-[#27314a] px-4 py-2 rounded-lg self-start"
                  onClick={() =>
                    setForm((current) => ({
                      ...current,
                      resources: recalculateResourceDays(current.resources, current.invoiceMonth),
                    }))
                  }
                >
                  Recalculate Days
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[920px] text-sm">
                  <thead>
                    <tr className="text-left text-[#6f7d97] border-b border-[#e6ebf6]">
                      <th className="py-2 pr-3">Employee Name</th>
                      <th className="py-2 pr-3">Resource Price</th>
                      <th className="py-2 pr-3">Days</th>
                      <th className="py-2 pr-3">Total Amount</th>
                      <th className="py-2 pr-3">Note</th>
                      <th className="py-2 pr-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.resources.map((resource) => (
                      <tr key={resource.id} className="border-b border-[#eef2fb]">
                        <td className="py-2 pr-3">
                          <input
                            className="search-input"
                            value={resource.employeeName}
                            onChange={(event) => updateResource(resource.id, "employeeName", event.target.value)}
                          />
                        </td>
                        <td className="py-2 pr-3">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="search-input"
                            value={resource.price}
                            onChange={(event) => updateResource(resource.id, "price", event.target.value)}
                          />
                        </td>
                        <td className="py-2 pr-3">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="search-input"
                            value={resource.quantity}
                            onChange={(event) => updateResource(resource.id, "quantity", event.target.value)}
                          />
                          <p className="mt-1 text-xs text-[#6f7d97]">
                            {resource.workingDays != null
                              ? `${resource.workingDays} working - ${resource.leaveDays || 0} leave`
                              : "Editable"}
                          </p>
                        </td>
                        <td className="py-2 pr-3 font-semibold text-[#27314a]">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="search-input"
                            value={resource.totalAmount ?? resource.price}
                            onChange={(event) => updateResource(resource.id, "totalAmount", event.target.value)}
                          />
                        </td>
                        <td className="py-2 pr-3">
                          <input
                            type="text"
                            className="search-input"
                            value={resource.note || ""}
                            placeholder="Optional note"
                            onChange={(event) => updateResource(resource.id, "note", event.target.value)}
                          />
                        </td>
                        <td className="py-2 pr-3 text-right">
                          <button
                            type="button"
                            className="invoice-danger-action-btn"
                            onClick={() => removeResource(resource.id)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td className="py-3 pr-3">
                        <Select
                          className="w-full"
                          options={employeeOptions}
                          value={resourceDraft.employeeId || undefined}
                          onChange={(value, option) =>
                            setResourceDraft((current) => ({
                              ...current,
                              employeeId: value || "",
                              employeeName: option?.label || "",
                            }))
                          }
                          placeholder="Select employee"
                          showSearch
                          allowClear
                          optionFilterProp="label"
                        />
                      </td>
                      <td className="py-3 pr-3">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="search-input"
                          placeholder="Price"
                          value={resourceDraft.price}
                          onChange={(event) =>
                            setResourceDraft((current) => ({ ...current, price: event.target.value }))
                          }
                        />
                      </td>
                      <td className="py-3 pr-3">
                        <input
                          type="text"
                          className="search-input"
                          placeholder="Optional note"
                          value={resourceDraft.note}
                          onChange={(event) =>
                            setResourceDraft((current) => ({ ...current, note: event.target.value }))
                          }
                        />
                      </td>
                      <td className="py-3 pr-3">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="search-input"
                          value={resourceDraft.quantity}
                          onChange={(event) =>
                            setResourceDraft((current) => ({ ...current, quantity: event.target.value }))
                          }
                        />
                      </td>
                      <td className="py-3 pr-3 text-[#6f7d97]">Manual resource</td>
                      <td className="py-3 pr-3">
                        <button
                          type="button"
                          onClick={addResource}
                          className="button bg-btn-primary text-white px-4 py-2 rounded-lg"
                        >
                          Add Resource
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {form.resources.length ? (
              <div className="mt-6">
                <div className="flex flex-col gap-1 mb-3">
                  <h3 className="text-base font-semibold text-[#27314a]">Timesheets</h3>
                  <p className="text-sm text-[#6f7d97]">
                    View each employee timesheet, update the {formatMonth(form.invoiceMonth) || "invoice month"} tab
                    manually in Google Sheets, then tap Attach to include it as a PDF with the invoice email.
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[680px] text-sm">
                    <thead>
                      <tr className="text-left text-[#6f7d97] border-b border-[#e6ebf6]">
                        <th className="py-2 pr-3">Employee</th>
                        <th className="py-2 pr-3">Status</th>
                        <th className="py-2 pr-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.resources.map((resource) => {
                        const attached = resource.employeeId
                          ? getAttachedTimesheet(resource.employeeId)
                          : null;
                        const isLoading = timesheetLoadingId === String(resource.employeeId || "");
                        return (
                          <tr key={`timesheet-${resource.id}`} className="border-b border-[#eef2fb]">
                            <td className="py-3 pr-3 text-[#27314a]">{resource.employeeName}</td>
                            <td className="py-3 pr-3">
                              {attached ? (
                                <span className="invoice-timesheet-badge invoice-timesheet-badge--attached">
                                  Attached
                                </span>
                              ) : (
                                <span className="invoice-timesheet-badge">Not attached</span>
                              )}
                            </td>
                            <td className="py-3 pr-3">
                              <div className="flex flex-wrap justify-end gap-2">
                                <button
                                  type="button"
                                  className="button border border-[#d7deed] bg-white text-[#27314a] px-3 py-1.5 rounded-lg text-sm"
                                  disabled={!resource.employeeId || isLoading}
                                  onClick={() => viewResourceTimesheet(resource)}
                                >
                                  {isLoading ? "Opening…" : "View Timesheet"}
                                </button>
                                {attached ? (
                                  <button
                                    type="button"
                                    className="invoice-danger-action-btn"
                                    onClick={() => requestDetachResourceTimesheet(resource)}
                                  >
                                    Detach
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="button bg-btn-primary text-white px-3 py-1.5 rounded-lg text-sm"
                                    disabled={!resource.employeeId || isLoading}
                                    onClick={() => attachResourceTimesheet(resource)}
                                  >
                                    {isLoading ? "Attaching…" : "Attach"}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}

            <div
              className={`mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 ${formGstBreakdown.intraState ? "lg:grid-cols-4" : "lg:grid-cols-5"
                }`}
            >
              <div className="card-border card-body">
                <p className="text-sm text-[#6f7d97]">Subtotal</p>
                <p className="text-lg font-semibold text-[#27314a]">{formatMoney(totals.subtotal)}</p>
              </div>
              {getInvoiceTaxComponents(formGstBreakdown).map(({ key, label }) => (
                <div key={key} className="card-border card-body">
                  <p className="text-sm text-[#6f7d97]">
                    {label} ({formatGstRate(formGstBreakdown[`${key}Rate`])})
                  </p>
                  <p className="text-lg font-semibold text-[#27314a]">
                    {formatMoney(formGstBreakdown[`${key}Amount`])}
                  </p>
                </div>
              ))}
              <div className="card-border card-body">
                <p className="text-sm text-[#6f7d97]">Total Amount</p>
                <p className="text-lg font-semibold text-[#27314a]">{formatMoney(totals.grandTotal)}</p>
              </div>
            </div>
            <p className="mt-3 text-sm text-[#6f7d97]">
              Amount in words:{" "}
              <span className="font-medium text-[#27314a]">{formatAmountInWords(totals.grandTotal)}</span>
            </p>

            <div className="flex flex-wrap justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={resetForm}
                disabled={saving}
                className="button px-5 py-2 rounded-lg border border-[#d9e2f3] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="button bg-btn-primary text-white px-5 py-2 rounded-lg disabled:opacity-50"
              >
                {saving
                  ? editingInvoiceId
                    ? "Updating…"
                    : "Saving…"
                  : editingInvoiceId
                    ? "Update Invoice"
                    : "Save & Generate Invoice"}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {!showForm ? (
        <div className="card-border card-body">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-[#27314a]">Invoices</h2>
              <p className="text-sm text-[#6f7d97]">
                Search by invoice number, client name, or company name.
              </p>
            </div>
            <div className="flex w-full flex-col gap-2 md:w-[360px]">
              <input
                className="search-input w-full !text-black"
                value={invoiceSearch}
                onChange={(event) => setInvoiceSearch(event.target.value)}
                placeholder="Search invoices, clients, companies"
              />
              {invoiceSearch || invoiceNumberFilter || clientFilter || companyFilter ? (
                <button
                  type="button"
                  className="self-end text-xs text-[#3867d6]"
                  onClick={() => {
                    setInvoiceSearch("");
                    setInvoiceNumberFilter("");
                    setClientFilter("");
                    setCompanyFilter("");
                  }}
                >
                  Clear filters
                </button>
              ) : null}
            </div>
          </div>
          <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
            <Select
              className="w-full invoice-filter-select"
              value={invoiceNumberFilter || undefined}
              onChange={(value) => setInvoiceNumberFilter(value || "")}
              options={invoiceNumberFilterOptions}
              placeholder="Filter by invoice number"
              showSearch
              allowClear
              optionFilterProp="label"
            />
            <Select
              className="w-full invoice-filter-select"
              value={clientFilter || undefined}
              onChange={(value) => setClientFilter(value || "")}
              options={invoiceClientFilterOptions}
              placeholder="Filter by client"
              showSearch
              allowClear
              optionFilterProp="label"
            />
            <Select
              className="w-full invoice-filter-select"
              value={companyFilter || undefined}
              onChange={(value) => setCompanyFilter(value || "")}
              options={invoiceCompanyFilterOptions}
              placeholder="Filter by company"
              showSearch
              allowClear
              optionFilterProp="label"
            />
          </div>
          {loading ? (
            <Loader className="!h-[30vh]" />
          ) : invoices.length ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-sm">
                <thead>
                  <tr className="text-left text-[#6f7d97] border-b border-[#e6ebf6]">
                    <th className="py-2 pr-3">Invoice Number</th>
                    <th className="py-2 pr-3">Invoice Date</th>
                    <th className="py-2 pr-3">Invoice Month</th>
                    <th className="py-2 pr-3">Client</th>
                    <th className="py-2 pr-3">Company</th>
                    <th className="py-2 pr-3">Subtotal</th>
                    <th className="py-2 pr-3">GST</th>
                    <th className="py-2 pr-3">Grand Total</th>
                    <th className="py-2 pr-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.length ? (
                    filteredInvoices.map((invoice) => (
                      <tr key={invoice.id} className="border-b border-[#eef2fb]">
                        <td className="py-3 pr-3 font-semibold text-[#27314a]">{invoice.invoiceNumber}</td>
                        <td className="py-3 pr-3 text-[#27314a]">{formatDate(invoice.invoiceDate)}</td>
                        <td className="py-3 pr-3 text-[#27314a]">{formatMonth(invoice.invoiceMonth)}</td>
                        <td className="py-3 pr-3 text-[#27314a]">{invoice.clientName}</td>
                        <td className="py-3 pr-3 text-[#27314a]">{invoice.companyName}</td>
                        <td className="py-3 pr-3 text-[#27314a]">{formatMoney(invoice.subtotal)}</td>
                        <td className="py-3 pr-3 text-[#27314a]">{formatMoney(invoice.gst)}</td>
                        <td className="py-3 pr-3 font-semibold text-[#27314a]">{formatMoney(invoice.grandTotal)}</td>
                        <td className="py-3 pr-3">
                          <Dropdown
                            menu={{
                              items: getInvoiceActionMenuItems(invoice),
                              onClick: ({ key }) => handleInvoiceAction(key, invoice),
                            }}
                            trigger={["click"]}
                            placement="bottomRight"
                          >
                            <button
                              type="button"
                              className="invoice-action-menu-btn"
                              aria-label={`Actions for invoice ${invoice.invoiceNumber}`}
                            >
                              <MoreOutlined />
                            </button>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="py-4 text-center text-[#6f7d97]" colSpan={9}>
                        No invoices match your search.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-sm text-[#6f7d97]">
              No invoices created yet. Create an invoice after adding company and client management details.
            </div>
          )}
        </div>
      ) : null}

      <ConfirmModal
        isOpen={Boolean(invoiceToDelete)}
        onClose={() => setInvoiceToDelete(null)}
        onConfirm={confirmDeleteInvoice}
        title="Delete Invoice"
        content={`Are you sure you want to delete invoice ${invoiceToDelete?.invoiceNumber || ""}?`}
      />

      <Modal
        title="Detach Timesheet"
        open={Boolean(timesheetToDetach)}
        onOk={confirmDetachResourceTimesheet}
        onCancel={() => setTimesheetToDetach(null)}
        okText="Yes"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>
          Are you sure you want to detach the sheet
          {timesheetToDetach?.employeeName ? ` for ${timesheetToDetach.employeeName}` : ""}?
        </p>
      </Modal>

      <Modal
        title="Send mail"
        open={showSendMailModal}
        onOk={handleSendInvoiceMail}
        onCancel={() => {
          if (sendingInvoiceMail) return;
          setShowSendMailModal(false);
          setInvoiceToSendMail(null);
          setInvoiceMailDraft({ subject: "", body: "", cc: "", bcc: "" });
        }}
        okText={sendingInvoiceMail ? "Sending…" : "Send email"}
        cancelText="Cancel"
        confirmLoading={sendingInvoiceMail}
        okButtonProps={{
          disabled:
            sendingInvoiceMail ||
            !getInvoiceClientEmail(invoiceToSendMail) ||
            !invoiceMailDraft.subject.trim() ||
            !invoiceMailDraft.body.trim(),
        }}
        cancelButtonProps={{ disabled: sendingInvoiceMail }}
        width={760}
        className="invoice-mail-preview-modal"
      >
        {invoiceToSendMail ? (
          (() => {
            const preview = getInvoiceMailPreview(invoiceToSendMail);
            return (
              <div className="invoice-mail-preview">
                {!getInvoiceClientEmail(invoiceToSendMail) ? (
                  <p className="invoice-mail-preview__warning">
                    Add client email in Client Management before sending.
                  </p>
                ) : null}
                <div className="invoice-mail-preview__meta">
                  <div>
                    <span className="invoice-mail-preview__label">To</span>
                    <span>{preview.to}</span>
                  </div>
                  <div>
                    <span className="invoice-mail-preview__label">CC</span>
                    <input
                      className="invoice-mail-preview__input"
                      value={invoiceMailDraft.cc}
                      onChange={(event) =>
                        setInvoiceMailDraft((current) => ({ ...current, cc: event.target.value }))
                      }
                      placeholder="Optional CC emails, comma separated"
                    />
                  </div>
                  <div>
                    <span className="invoice-mail-preview__label">BCC</span>
                    <input
                      className="invoice-mail-preview__input"
                      value={invoiceMailDraft.bcc}
                      onChange={(event) =>
                        setInvoiceMailDraft((current) => ({ ...current, bcc: event.target.value }))
                      }
                      placeholder="Optional BCC emails, comma separated"
                    />
                  </div>
                  <div>
                    <span className="invoice-mail-preview__label">Subject</span>
                    <input
                      className="invoice-mail-preview__input"
                      value={invoiceMailDraft.subject}
                      onChange={(event) =>
                        setInvoiceMailDraft((current) => ({ ...current, subject: event.target.value }))
                      }
                      placeholder="Email subject"
                    />
                  </div>
                </div>
                <div className="invoice-mail-preview__body">
                  <textarea
                    className="invoice-mail-preview__textarea"
                    value={invoiceMailDraft.body}
                    onChange={(event) =>
                      setInvoiceMailDraft((current) => ({ ...current, body: event.target.value }))
                    }
                    rows={12}
                    placeholder="Email message"
                  />
                </div>
                <div className="invoice-mail-preview__attachments">
                  <span className="invoice-mail-preview__label">Attachments</span>
                  <ul>
                    {preview.attachments.map((name) => (
                      <li key={name}>{name}</li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })()
        ) : null}
      </Modal>
    </div>
  );
};

export default InvoiceManagementPage;
